// src/components/FinancialPromotions/FinancialPromotionContentGenerator.jsx

import React, { useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import {
  generateFinancialPromotion,
  getFinancialPromotionResponses,
} from "../services/financialPromotionsApi";
import NewInputField from "../../../DesignSystem/Inputs/NewInputField";
import NewInputTextArea from "../../../DesignSystem/Inputs/NewTextArea";
import Sidebar from "../../../DesignSystem/Sidebar";
import Breadcrumb from "../../../DesignSystem/Breadcrumb/Breadcrumb";
import CheckboxMultiSelect from "../../../DesignSystem/Dropdowns/CheckboxMultiSelect";
import CustomSelect from "../../../DesignSystem/Dropdowns/CustomSelect";
import PrimaryButton from "../../../DesignSystem/Buttons/PrimaryButton";
import SecondaryButton from "../../../DesignSystem/Buttons/SecondaryButton";
import { useAdviserListWithUnappointed } from "../../../../context/Adviser/Utils/UnappointedAppointedAdviserEmailList";
import { APP_ROUTES } from "../../../../constants/appRoutes";
import "../../../../css/Marketing/FinancialPromotionContentGenerator/FinancialPromotionContentGenerator.css";
import Banner from "../../../DesignSystem/Banner/Banner";
import {ReactComponent as IconCopy} from "../../../../assets/icons/financialPromotions/Copy.svg";

const initialForm = {
  adviserEmail: "",
  contentTypes: [""],
  platforms: [""],
  audience: "",
  objective: "",
  tone: "Professional, friendly and educational",
  notes: "",
};
const clearedForm = {
  adviserEmail: "",
  contentTypes: [],
  platforms: [],
  audience: "",
  objective: "",
  tone: "",
  notes: "",
};

const contentTypeOptions = [
  { value: "mortgage", label: "Mortgages" },
  { value: "equity_release", label: "Equity Release" },
  {
    value: "pensions_and_investments",
    label: "Pensions and Investments",
  },
  { value: "investments", label: "Investments" },
  { value: "pensions", label: "Pensions" },
  { value: "protection", label: "Protection" },
  { value: "other", label: "Other" },
];

const platformOptions = [
  { value: "LinkedIn", label: "LinkedIn" },
  { value: "Facebook", label: "Facebook" },
  { value: "Instagram", label: "Instagram" },
  { value: "X", label: "X" },
];

const breadcrumbItems = [
  { label: "Staff Portal", to: APP_ROUTES.DEPARTMENT_ALL_STAFF },
  { label: "Marketing", to: APP_ROUTES.DEPARTMENT_MARKETING },
  { label: "Social Media Content Management", to: APP_ROUTES.SOCIAL_MEDIA_CONTENT_MANAGEMENT },
  { label: "Generate New Content" },
];

const formatDateTime = (dateValue) => {
  if (!dateValue) return "Not available";

  const parsedDate = new Date(dateValue);

  if (Number.isNaN(parsedDate.getTime())) {
    return "Not available";
  }

  return parsedDate.toLocaleString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const createMessageId = () => {
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const getDisplayName = (adviser, fallback = "") => {
  return (
    adviser?.tradingName ||
    adviser?.adviserName ||
    adviser?.name ||
    adviser?.email ||
    fallback
  );
};

const RequiredMark = () => (
  <span className="fp-required" aria-hidden="true">
    *
  </span>
);

const FieldError = ({ id, children }) => {
  if (!children) return null;

  return (
    <p id={id} className="fp-field-error">
      {children}
    </p>
  );
};

const EmptyState = ({ title, description }) => {
  return (
    <div className="fp-empty-state">
      <p className="FP_Content primary">{title}</p>
      <span className="FP_Help_Text secondary">{description}</span>
    </div>
  );
};

const CopyMiniButton = ({ text }) => {
  if (!text) return null;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (error) {
      // console.error("Failed to copy text:", error);
    }
  };

  return (
    <button
      type="button"
      className="fp-mini-copy-button"
      onClick={handleCopy}
      aria-label="Copy text"
      title="Copy"
    >
      <IconCopy />
    
    </button>
  );
};

const ResponseContentSection = ({ title, children, copyText }) => {
  if (!children) return null;

  return (
    <section className="fp-response-content-section">
      <div className="fp-section-title-row">
        <div className="FP_Content_Semibold text-black">{title}</div>
        <CopyMiniButton text={copyText || children} />
      </div>

      <p className="fp-copyable-text text-black FP_Content">{children}</p>
    </section>
  );
};

const GeneratedResponsePreview = ({ message, index }) => {
  if (message.loading) {
    return (
      <div className="fp-response-loading-state">
        <span className="fp-spinner" aria-hidden="true" />

        <p className="FP_Content_Semibold primary">Generating Content</p>

        {/* <span className="FP_Help_Text secondary">
          Using the current brief and previous generated posts as context.
        </span> */}
      </div>
    );
  }

  if (message.error) {
    return (
      <article className="fp-response-preview-card fp-chat-message--error">
        <div className="fp-response-preview-card__header">
          <div>
            <h3 className="FP_Content_Semibold primary">
              {message.title || "Generation failed"}
            </h3>

            <p className="FP_Help_Text secondary">{message.message}</p>
          </div>
        </div>
      </article>
    );
  }

const generated = message.result?.content || {};
const contentType = message.result?.contentType || message.payload?.contentType;
const platform = message.result?.platform || message.payload?.platform;

const captionText =
  generated.caption || generated.shortCaption || message.message || "";

const hashtagText = Array.isArray(generated.hashtags)
  ? generated.hashtags.join(" ")
  : "";

const captionWithHashtags = [captionText, hashtagText]
  .filter(Boolean)
  .join("\n\n");

  return (
    <article className="fp-response-preview-card">
      <div className="fp-response-preview-card__header">
        <div>
          <h3 className="FP_Content_Semibold text-black">
            Response {index + 1}
          </h3>

          <p className="FP_Help_Text secondary">
            {contentType || platform
              ? `${contentType || "Content"} / ${platform || "Platform"}`
              : message.title}
          </p>
        </div>

        <time className="FP_Help_Text secondary" dateTime={message.createdAt}>
          {formatDateTime(message.createdAt)}
        </time>
      </div>

      <ResponseContentSection title="Caption" copyText={captionWithHashtags}>
        {captionWithHashtags}
      </ResponseContentSection>
      <ResponseContentSection
        title="Regulatory Text"
        copyText={generated.regulatoryTextUsed}
      >
        {generated.regulatoryTextUsed}
      </ResponseContentSection>

      <ResponseContentSection
        title="Graphic Text"
        copyText={generated.graphicText}
      >
        {generated.graphicText}
      </ResponseContentSection>

      <ResponseContentSection
        title="Image Brief"
        copyText={generated.suggestedImageBrief}
      >
        {generated.suggestedImageBrief}
      </ResponseContentSection>

      {/* {generated.hashtags?.length > 0 && (
        <ResponseContentSection
          title="Hashtags"
          copyText={generated.hashtags.join(" ")}
        >
          {generated.hashtags.join(" ")}
        </ResponseContentSection>
      )} */}
    </article>
  );
};

const getStoredGeneratorPrefill = () => {
  try {
    const stored = sessionStorage.getItem("financialPromotionGeneratorPrefill");

    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

const normaliseGeneratorArray = (value, fallback = []) => {
  if (Array.isArray(value)) return value.filter(Boolean);

  if (typeof value === "string" && value.trim()) {
    return value
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  }

  return fallback;
};

const getInitialGeneratorForm = (prefill) => {
  if (!prefill) return initialForm;

  return {
    adviserEmail: prefill.adviserEmail || "",
    contentTypes: normaliseGeneratorArray(
      prefill.contentTypes,
      initialForm.contentTypes,
    ),
    platforms: normaliseGeneratorArray(
      prefill.platforms,
      initialForm.platforms,
    ),
    audience: prefill.audience || "",
    objective: prefill.objective || "",
    tone: prefill.tone || initialForm.tone,
    notes: prefill.notes || "",
  };
};

const getPrefillResponses = (prefill) => {
  if (Array.isArray(prefill?.existingResponses)) {
    return prefill.existingResponses.filter(Boolean);
  }

  if (prefill?.existingResponse) {
    return [prefill.existingResponse];
  }

  return [];
};

const getInitialGenerationContext = (prefill) => {
  const existingResponses = getPrefillResponses(prefill);

  return existingResponses.map((response) => ({
    adviserEmail: prefill.adviserEmail || "",
    contentType:
      response.contentType ||
      normaliseGeneratorArray(prefill.contentTypes).join(", "),
    platform:
      response.platform ||
      normaliseGeneratorArray(prefill.platforms).join(", "),
    audience: response.audience || prefill.audience || "",
    objective: response.objective || prefill.objective || "",
    tone: response.tone || prefill.tone || "",
    notes: response.notes || prefill.notes || "",
    caption: response.caption || "",
    shortCaption: response.shortCaption || "",
    graphicText: response.graphicText || "",
    suggestedImageBrief: response.suggestedImageBrief || "",
    regulatoryTextUsed: response.regulatoryTextUsed || "",
    hashtags: response.hashtags || [],
    complianceNotes: response.complianceNotes || [],
    warnings: response.warnings || [],
  }));
};

const getInitialChatMessages = (prefill) => {
  const existingResponses = getPrefillResponses(prefill);

  return existingResponses.map((response, index) => ({
    id: createMessageId(),
    role: "assistant",
    title: `Existing response ${existingResponses.length - index}`,
    message:
      response.caption || response.shortCaption || "Existing response loaded.",
    createdAt:
      response.createdAt || response.updatedAt || new Date().toISOString(),
    payload: {
      contentType:
        response.contentType ||
        normaliseGeneratorArray(prefill.contentTypes).join(", "),
      platform:
        response.platform ||
        normaliseGeneratorArray(prefill.platforms).join(", "),
    },
    result: {
      contentType:
        response.contentType ||
        normaliseGeneratorArray(prefill.contentTypes).join(", "),
      platform:
        response.platform ||
        normaliseGeneratorArray(prefill.platforms).join(", "),
      content: response,
    },
  }));
};

const FinancialPromotionContentGenerator = () => {
  const location = useLocation();
  const errorRef = useRef(null);

 const generatorPrefill = useMemo(() => {
   if (location.state?.resetGeneratorPrefill) {
     return null;
   }

   return location.state?.generatorPrefill || getStoredGeneratorPrefill();
 }, [location.state]);
  
  useEffect(() => {
    if (location.state?.resetGeneratorPrefill) {
      sessionStorage.removeItem("financialPromotionGeneratorPrefill");
    }
  }, [location.state]);

  const isEditingExistingBrief = Boolean(generatorPrefill);

  const [activePromotionId, setActivePromotionId] = useState(
    () => generatorPrefill?.promotionId || "",
  );

  const [form, setForm] = useState(() =>
    getInitialGeneratorForm(generatorPrefill),
  );

  const [chatMessages, setChatMessages] = useState(() =>
    getInitialChatMessages(generatorPrefill),
  );

  const [generationContext, setGenerationContext] = useState(() =>
    getInitialGenerationContext(generatorPrefill),
  );

  const [savedResponseCount, setSavedResponseCount] = useState(0);
  const [loadingSavedResponses, setLoadingSavedResponses] = useState(false);
  const [generating, setGenerating] = useState(false);

  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [adviserError, setAdviserError] = useState("");

  const { advisers, loadingAdvisers } = useAdviserListWithUnappointed();

  const adviserOptions = useMemo(() => {
    return [
      {
        id: "",
        name: loadingAdvisers ? "Loading advisers..." : "Select Adviser",
      },
      ...advisers.map((adv) => ({
        id: adv.email,
        name: `${adv.email} ${adv.name ? `(${adv.name})` : ""}`,
      })),
    ];
  }, [advisers, loadingAdvisers]);

  const selectedAdviser = useMemo(() => {
    return advisers.find((item) => item.email === form.adviserEmail);
  }, [advisers, form.adviserEmail]);

  const totalPostsToGenerate = useMemo(() => {
    return form.contentTypes.length && form.platforms.length ? 1 : 0;
  }, [form.contentTypes, form.platforms]);

  const getMessageTime = (message) => {
    const time = new Date(message.createdAt || 0).getTime();
    return Number.isNaN(time) ? 0 : time;
  };

  const responseMessages = useMemo(() => {
    return chatMessages
      .filter((message) => message.loading || message.result || message.error)
      .sort((a, b) => getMessageTime(b) - getMessageTime(a));
  }, [chatMessages]);

  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.focus();
    }
  }, [error]);

  useEffect(() => {
    const loadSavedResponseCount = async () => {
      try {
        setLoadingSavedResponses(true);

        const data = await getFinancialPromotionResponses();
        setSavedResponseCount(Array.isArray(data) ? data.length : 0);
      } catch (err) {
        setError(err.message || "Failed to load saved responses.");
      } finally {
        setLoadingSavedResponses(false);
      }
    };

    loadSavedResponseCount();
  }, []);

  const refreshSavedResponseCount = async () => {
    const data = await getFinancialPromotionResponses();
    setSavedResponseCount(Array.isArray(data) ? data.length : 0);
  };

  const clearFieldError = (name) => {
    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const updateFormField = (name, value) => {
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));

    clearFieldError(name);
    setError("");
  };

  const handleMultiSelectChange = (name, nextValue) => {
    setForm((prev) => ({
      ...prev,
      [name]: Array.isArray(nextValue) ? nextValue : [],
    }));

    clearFieldError(name);
    setError("");
  };

  const handleClearForm = () => {
    sessionStorage.removeItem("financialPromotionGeneratorPrefill");
    setForm(clearedForm);
    setChatMessages([]);
    setGenerationContext([]);
    setFieldErrors({});
    setAdviserError("");
    setError("");
    setActivePromotionId("");
  };

  const addChatMessage = (message) => {
    setChatMessages((prev) => [
      ...prev,
      {
        id: createMessageId(),
        createdAt: new Date().toISOString(),
        ...message,
      },
    ]);
  };

  const removeLoadingMessage = (loadingId) => {
    setChatMessages((prev) =>
      prev.filter((message) => message.id !== loadingId),
    );
  };

  const validate = () => {
    const nextErrors = {};

    if (!form.adviserEmail) {
      nextErrors.adviserEmail = "Please select an adviser.";
    }

    if (!form.contentTypes.length) {
      nextErrors.contentTypes = "Please select at least one content type.";
    }

    if (!form.platforms.length) {
      nextErrors.platforms = "Please select at least one platform.";
    }

    if (!form.audience.trim()) {
      nextErrors.audience = "Please enter the intended audience.";
    }

    if (!form.objective.trim()) {
      nextErrors.objective = "Please enter the campaign objective.";
    }

    return nextErrors;
  };

  const buildRequestPayloads = () => {
    return [
      {
        promotionId: activePromotionId || null,
        adviserEmail: form.adviserEmail,

        contentType: form.contentTypes.join(", "),
        contentTypes: form.contentTypes,

        platform: form.platforms.join(", "),
        platforms: form.platforms,

        audience: form.audience.trim(),
        objective: form.objective.trim(),
        tone: form.tone.trim(),
        notes: form.notes.trim(),

        mode: activePromotionId ? "update" : "create",
      },
    ];
  };

  const handleGenerate = async (event) => {
    event?.preventDefault();

    const nextErrors = validate();
    setFieldErrors(nextErrors);

    if (Object.keys(nextErrors).length) {
      setError("Please enter all required fields.");
      return;
    }

    try {
      setGenerating(true);
      // setChatMessages([]);
      // setGenerationContext([]);
      setError("");

      const payloads = buildRequestPayloads();
      const nextContext = [];

      addChatMessage({
        role: "user",
        title: "Generation request",
        message: `Create one combined post for ${getDisplayName(
          selectedAdviser,
          form.adviserEmail,
        )} using: ${form.contentTypes.join(", ")} for ${form.platforms.join(", ")}.`,
      });

      for (const payload of payloads) {
        const loadingId = createMessageId();

        setChatMessages((prev) => [
          ...prev,
          {
            id: loadingId,
            role: "assistant",
            title: `Generating post`,
            message:
              "Using the current campaign brief and previous generated posts as context.",
            loading: true,
            createdAt: new Date().toISOString(),
            payload,
          },
        ]);

        const result = await generateFinancialPromotion({
          ...payload,
          promotionId: activePromotionId || null,
          conversationContext: generationContext,
        });

        if (result?.promotionId && !activePromotionId) {
          setActivePromotionId(result.promotionId);
        }

        removeLoadingMessage(loadingId);

        const normalisedResult = {
          ...result,
          contentType: result?.contentType || payload.contentType,
          platform: result?.platform || payload.platform,
        };

        const generated = normalisedResult?.content || {};

        const contextItem = {
          adviserEmail: payload.adviserEmail,
          contentType: payload.contentType,
          platform: payload.platform,
          audience: payload.audience,
          objective: payload.objective,
          tone: payload.tone,
          notes: payload.notes,
          caption: generated.caption || "",
          shortCaption: generated.shortCaption || "",
          graphicText: generated.graphicText || "",
          suggestedImageBrief: generated.suggestedImageBrief || "",
          regulatoryTextUsed: generated.regulatoryTextUsed || "",
          hashtags: generated.hashtags || [],
          complianceNotes: generated.complianceNotes || [],
          warnings: generated.warnings || [],
        };

        setGenerationContext((prev) => [...prev, contextItem]);

        // nextContext.push(contextItem);
        // setGenerationContext([...nextContext]);

        addChatMessage({
          role: "assistant",
          title: `${payload.contentType} / ${payload.platform}`,
          message: generated.caption || "Draft generated successfully.",
          result: normalisedResult,
          payload,
        });
      }

      await refreshSavedResponseCount();
    } catch (err) {
      setError(err.message || "Failed to generate content.");

      addChatMessage({
        role: "assistant",
        title: "Generation failed",
        message:
          err.message || "Something went wrong while generating content.",
        error: true,
      });
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="main-content">
      <Sidebar />

      <Helmet>
        <title>Generate New Content</title>
        <meta
          name="description"
          content="Generate financial promotion content drafts for advisers based on saved regulatory text and campaign briefs."
        />
      </Helmet>

      <main className="Main-Container gap-xl column">
        <Breadcrumb items={breadcrumbItems} />

        <section className="fp-generator-header">
          <div>
            <div className="fp-generator-title-row">
              <h3 className="FP_H3 primary">
                {isEditingExistingBrief
                  ? "Edit Content Brief"
                  : "Generate New Content"}
              </h3>
            </div>

            {/* <p className="FP_Help_Text secondary">
              Generate adviser-specific, review-ready draft content. All
              generated content will be saved as a new entry on the dashboard.
              Saved content can then be sent to the Financial Promotions
              approval process to create social media posts for advisers.
            </p> */}

            {/* <p className="FP_Help_Text secondary fp-generator-saved-count">
              {loadingSavedResponses
                ? "Loading saved responses..."
                : `${savedResponseCount} saved response${
                    savedResponseCount === 1 ? "" : "s"
                  } found.`}
            </p> */}
          </div>
        </section>

        {error && (
          <Banner type="error" ref={errorRef} tabIndex={-1} message={error} />
        )}

        <section className="fp-generator-layout">
          <form
            className="fp-brief-card fp-generator-brief-card gap-m column"
            onSubmit={handleGenerate}
            noValidate
            aria-busy={generating}
          >
            <div className="fp-card-header">
              <div>
                <h2 className="FP_Subtitle text-black">
                  {isEditingExistingBrief
                    ? "Edit Generation Brief"
                    : "Content Brief"}
                </h2>

                {/* <p className="FP_Help_Text secondary">
                  Fields marked with <RequiredMark /> are required.
                </p> */}
              </div>
            </div>

            <div className="fp-form-column gap-xl column">
              {/* <label className="FP_Body">
                Adviser <span className="fp-form-required">*</span>
              </label> */}

              <CustomSelect
                label="Adviser"
                required
                options={adviserOptions}
                value={form.adviserEmail}
                onChange={(e) => {
                  updateFormField("adviserEmail", e.target.value);

                  if (e.target.value) {
                    setAdviserError("");
                  }
                }}
                disabled={loadingAdvisers || generating}
                error={Boolean(fieldErrors.adviserEmail || adviserError)}
                placeholder={
                  loadingAdvisers ? "Loading advisers..." : "Select Adviser"
                }
                style={{
                  marginBottom:
                    fieldErrors.adviserEmail || adviserError ? 0 : 16,
                }}
              />

              <FieldError id="fp-adviserEmail-error">
                {fieldErrors.adviserEmail || adviserError}
              </FieldError>

              <CheckboxMultiSelect
                label="What is this content about?"
                required
                value={form.contentTypes}
                options={contentTypeOptions}
                placeholder="Select content types"
                helperText="Select one or more content types."
                disabled={generating}
                onChange={(nextValue) =>
                  handleMultiSelectChange("contentTypes", nextValue)
                }
              />

              <FieldError id="fp-contentTypes-error">
                {fieldErrors.contentTypes}
              </FieldError>

              <CheckboxMultiSelect
                label="Platform"
                required
                value={form.platforms}
                options={platformOptions}
                placeholder="Select platforms"
                helperText="Select one or more platforms."
                disabled={generating}
                onChange={(nextValue) =>
                  handleMultiSelectChange("platforms", nextValue)
                }
              />

              <FieldError id="fp-platforms-error">
                {fieldErrors.platforms}
              </FieldError>

              <NewInputField
                id="fp-audience"
                label="Audience"
                required
                value={form.audience}
                onChange={(e) => updateFormField("audience", e.target.value)}
                placeholder="e.g. First-time buyers"
                helperText="Example: first-time buyers, families, business owners."
                error={fieldErrors.audience}
                disabled={generating}
              />

              <NewInputField
                id="fp-tone"
                label="Tone"
                value={form.tone}
                onChange={(e) => updateFormField("tone", e.target.value)}
                placeholder="e.g. Professional and friendly"
                helperText="This controls the writing style of the generated posts."
                disabled={generating}
              />

              <NewInputField
                id="fp-objective"
                label="Objective"
                required
                value={form.objective}
                onChange={(e) => updateFormField("objective", e.target.value)}
                placeholder="e.g. Create an educational post about pensions"
                helperText="Be specific about what the post should achieve."
                error={fieldErrors.objective}
                disabled={generating}
              />

              <NewInputTextArea
                id="fp-notes"
                label="Extra Notes"
                value={form.notes}
                onChange={(e) => updateFormField("notes", e.target.value)}
                placeholder="Optional extra context, key points, offers, compliance notes or wording preferences"
                helperText="Optional context, key points, offers, compliance notes or wording preferences."
                error={fieldErrors.notes}
                disabled={generating}
                maxLength={1000}
              />
            </div>

            <div className="fp-generator-actions">
              <SecondaryButton
                type="button"
                label="Clear All"
                onClick={handleClearForm}
                disabled={generating}
              />

              <PrimaryButton
                type="submit"
                className="fp-primary-button"
                disabled={generating || loadingAdvisers}
                label={
                  generating
                    ? "Generating..."
                    : isEditingExistingBrief
                      ? "Regenerate"
                      : "Generate"
                }
              />
            </div>
          </form>

          <aside
            className="fp-panel fp-generator-responses"
            aria-labelledby="fp-responses-title"
          >
            <div className="fp-card-header">
              <div>
                <h2 id="fp-responses-title" className="FP_Subtitle text-black">
                  Responses
                </h2>

                {/* <p className="FP_Help_Text secondary">
                  Generated content appears here as each post is completed.
                </p> */}
              </div>

              {/* {(generating || generationContext.length > 0) && (
                <span className="fp-context-pill">
                  {generationContext.length} of {totalPostsToGenerate}
                </span>
              )} */}
            </div>

            {responseMessages.length === 0 && !generating && (
              <EmptyState
                title="No content generated yet."
                description="Complete the brief and click Generate to create review-ready content."
              />
            )}

            <div className="fp-response-preview-list">
              {responseMessages.map((message, index) => (
                <GeneratedResponsePreview
                  key={message.id}
                  message={message}
                  index={responseMessages.length - index - 1}
                />
              ))}
            </div>
          </aside>
        </section>

        <div className="fp-sr-only" aria-live="polite">
          {generating
            ? `Generating ${generationContext.length} of ${totalPostsToGenerate} posts.`
            : ""}
        </div>
      </main>
    </div>
  );
};

export default FinancialPromotionContentGenerator;
